import { useMemo, type CSSProperties } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { Block, PlanVanAanpakBlockContent } from '@/types';
import { uboTheme } from '@/theme/uboTheme';
import { buildSectionCss } from '@/theme/sectionStyles';
import RichTextEditor from './RichTextEditor';
import PageHeader from './PageHeader';
import { useQuoteClientLogo, useQuotePageBrand, useResolvedAgencyLogoUrl } from '@/store/quoteClientLogoContext';
import { contentAreaHeightPxForBrand } from '@/constants/a4';

export type PlanVanAanpakContent = PlanVanAanpakBlockContent;

export function buildPlanVanAanpakCss(blockId: string): string {
  return buildSectionCss(`.plan-readonly-${blockId}`, 'default', uboTheme.colors.secondary);
}

interface PlanVanAanpakBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
  /** Publieke offerte: geen overflow-hidden op read-only root. */
  isPublic?: boolean;
  printFragmentIndex?: number;
  printFragmentCount?: number;
}

function PlanVanAanpakBlock({
  block,
  isEditable = false,
  onUpdate,
  isPublic = false,
  printFragmentIndex = 0,
  printFragmentCount = 1,
}: PlanVanAanpakBlockProps) {
  const content = block.content as PlanVanAanpakContent;
  const quoteClientLogo = useQuoteClientLogo();
  const quoteBrand = useQuotePageBrand();
  const effectiveTheme = content.theme ?? quoteBrand;
  const pageBrand = effectiveTheme === 'ubx' ? 'ubx' : 'ubo';
  const agencyUrl = useResolvedAgencyLogoUrl(content.agencyLogoUrl, content.theme);
  const readonlyCss = useMemo(
    () => buildSectionCss(`.plan-readonly-${block.id}`, 'default', uboTheme.colors.secondary),
    [block.id],
  );
  const isContinuation = isPublic && printFragmentCount > 1 && printFragmentIndex > 0;
  const contentBodyHeight = contentAreaHeightPxForBrand(pageBrand);

  if (isEditable) {
    return (
      <div
        className="w-full h-full flex-1 relative overflow-hidden flex flex-col"
        style={{
          backgroundColor: '#ffffff',
          color: uboTheme.colors.secondary,
          padding: '50px',
        }}
      >
        {/* Logo */}
        <PageHeader
          clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
          agencyLogoUrl={agencyUrl}
          brand={pageBrand}
          variant="light"
          isEditable={isEditable}
          onClientLogoChange={(url) => onUpdate?.({ content: { ...content, clientLogoUrl: url } })}
        />

        {/* Title */}
        <input
          value={content.title || ''}
          onChange={(e) => onUpdate?.({ content: { ...content, title: e.target.value } })}
          placeholder="PLAN VAN AANPAK"
          className="w-full bg-transparent border-none outline-none mb-6"
          style={{
            fontSize: '32px',
            fontWeight: 900,
            textTransform: 'uppercase',
            fontFamily: uboTheme.fonts.primaryFallback,
            color: uboTheme.colors.primary,
          }}
        />

        {/* Company Data Toggle */}
        <div className="flex items-center gap-3 mb-4">
          <Switch
            checked={content.showCompanyData ?? true}
            onCheckedChange={(checked) => onUpdate?.({ content: { ...content, showCompanyData: checked } })}
          />
          <Label>Toon bedrijfsgegevens</Label>
        </div>

        {/* Company Data Fields */}
        {content.showCompanyData !== false && (
          <div className="mb-6 flex flex-col gap-2">
            <Input
              placeholder="Bedrijfsnaam"
              value={content.companyName || ''}
              onChange={(e) => onUpdate?.({ content: { ...content, companyName: e.target.value } })}
              className="h-8"
            />
            <Input
              placeholder="Adres"
              value={content.companyAddress || ''}
              onChange={(e) => onUpdate?.({ content: { ...content, companyAddress: e.target.value } })}
              className="h-8"
            />
            <div className="flex gap-2">
              <Input
                placeholder="Postcode"
                value={content.companyZip || ''}
                onChange={(e) => onUpdate?.({ content: { ...content, companyZip: e.target.value } })}
                className="h-8 w-[120px]"
              />
              <Input
                placeholder="Plaats"
                value={content.companyCity || ''}
                onChange={(e) => onUpdate?.({ content: { ...content, companyCity: e.target.value } })}
                className="h-8 flex-1"
              />
            </div>
          </div>
        )}

        {/* Rich Text Content */}
        <div className="flex-1">
          <RichTextEditor
            content={content.content || ''}
            onChange={(html) => onUpdate?.({ content: { ...content, content: html } })}
            placeholder="Schrijf hier je plan van aanpak..."
            minHeight={200}
          />
        </div>
      </div>
    );
  }

  // Read-only view
  return (
    <div
      className={`w-full h-full flex-1 relative flex flex-col ${isPublic ? 'overflow-visible' : 'overflow-hidden'}`}
      data-print-continuation={isContinuation ? 'true' : undefined}
      style={{
        backgroundColor: '#ffffff',
        color: uboTheme.colors.secondary,
        padding: '50px',
        pageBreakAfter: isPublic ? 'auto' : 'always',
      }}
    >
      {/* Logo */}
      <PageHeader
        clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
        agencyLogoUrl={agencyUrl}
        brand={pageBrand}
        variant="light"
      />

      {!isContinuation && (
        <>
          {/* Title */}
          <h2
            className="mb-6"
            style={{
              fontSize: '32px',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontFamily: uboTheme.fonts.primaryFallback,
              color: uboTheme.colors.primary,
            }}
          >
            {content.title || 'Plan van Aanpak'}
          </h2>

          {/* Company Data */}
          {content.showCompanyData !== false && content.companyName && (
            <div className="mb-6">
              <p className="font-bold">{content.companyName}</p>
              {content.companyAddress && (
                <p className="text-sm">{content.companyAddress}</p>
              )}
              {(content.companyZip || content.companyCity) && (
                <p className="text-sm">
                  {content.companyZip} {content.companyCity}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Content */}
      <div
        className={`flex-1 plan-readonly-${block.id}`}
        style={{
          ...(isContinuation ? { marginTop: 24 } : {}),
          '--quote-content-page-body-height': `${contentBodyHeight}px`,
        } as CSSProperties}
        dangerouslySetInnerHTML={{ __html: content.content || '' }}
      />
      <style>{readonlyCss}</style>
    </div>
  );
}

export default PlanVanAanpakBlock;
